import React from 'react'
import { cn } from '@/utils/cn'

interface TutaraCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'bordered' | 'elevated' | 'glass'
  padding?: 'none' | 'sm' | 'md' | 'lg'
}

const TutaraCard = React.forwardRef<HTMLDivElement, TutaraCardProps>(
  ({ className, variant = 'default', padding = 'md', children, ...props }, ref) => {
    const variants = {
      default: 'bg-white dark:bg-gray-800 text-tutara-charcoal dark:text-gray-200',
      bordered: 'bg-white dark:bg-gray-800 text-tutara-charcoal dark:text-gray-200 border border-gray-200 dark:border-gray-700',
      elevated: 'bg-white dark:bg-gray-800 text-tutara-charcoal dark:text-gray-200 shadow-lg',
      glass: 'backdrop-blur-md bg-white/30 dark:bg-gray-900/30 border border-white/20 dark:border-gray-800/20',
    }

    const paddings = {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    }

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-lg transition-all',
          variants[variant],
          paddings[padding],
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

TutaraCard.displayName = 'TutaraCard'

const TutaraCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5', className)}
    {...props}
  />
))

TutaraCardHeader.displayName = 'TutaraCardHeader'

const TutaraCardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('font-semibold text-xl leading-none tracking-tight', className)}
    {...props}
  />
))

TutaraCardTitle.displayName = 'TutaraCardTitle'

const TutaraCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-tutara-charcoal/70 dark:text-gray-300', className)}
    {...props}
  />
))

TutaraCardDescription.displayName = 'TutaraCardDescription'

const TutaraCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('pt-0', className)} {...props} />
))

TutaraCardContent.displayName = 'TutaraCardContent'

const TutaraCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center pt-4', className)}
    {...props}
  />
))

TutaraCardFooter.displayName = 'TutaraCardFooter'

export {
  TutaraCard,
  TutaraCardHeader,
  TutaraCardTitle,
  TutaraCardDescription,
  TutaraCardContent,
  TutaraCardFooter,
}